/* Shared presentational pieces for the Asset Request (ticket) screens. */
import dayjs from "dayjs";
import { Inbox } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";
import {
  assetReqStatusClass,
  assetReqPriorityClass,
  assetReqTypeClass,
  shortType,
} from "../../constants/assetRequest";

const pill = "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap";

export const StatusBadge = ({ status }) => (
  <span className={cn(pill, assetReqStatusClass(status))}>
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {status || "-"}
  </span>
);

export const PriorityBadge = ({ priority }) => (
  <span className={cn(pill, assetReqPriorityClass(priority))}>{priority || "-"}</span>
);

export const TypeBadge = ({ type, full = false }) => (
  <span className={cn(pill, assetReqTypeClass(type))}>{full ? type : shortType(type)}</span>
);

// Backend dates arrive as ISO strings; render compact, fall back to "—".
export const fmtDateTime = (d) =>
  d ? dayjs(d).format("DD MMM, hh:mm A") : "—";
export const fmtDate = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—");

export const EmptyState = ({ label, Icon = Inbox }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
    <Icon className="h-9 w-9 opacity-40" />
    <p className="text-sm">{label}</p>
  </div>
);

// Vertical timeline from an approval_log array of { created_at, action, by }.
export const Timeline = ({ log = [] }) => {
  const items = [...log].sort(
    (a, b) => new Date(a.created_at || a.at) - new Date(b.created_at || b.at)
  );
  if (items.length === 0) return <EmptyState label="No activity yet." />;
  return (
    <div className="relative pl-1">
      {items.map((e, i) => {
        const last = i === items.length - 1;
        return (
          <div key={i} className={cn("flex gap-3", !last && "pb-4")}>
            <div className="flex flex-col items-center">
              <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-sidebar-primary ring-4 ring-sidebar-primary/15" />
              {!last && <span className="mt-1 w-0.5 flex-1 bg-gray-200 dark:bg-gray-700" />}
            </div>
            <div className="-mt-0.5">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 leading-tight">
                {e.action || e.label}
              </p>
              <p className="text-xs text-muted-foreground">
                {fmtDateTime(e.created_at || e.at)} · {e.by || e.who || "—"}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// A small key/value detail list used in both drawer + admin detail sidebars.
export const DetailRows = ({ rows }) => (
  <div>
    {rows.map(([k, v]) => (
      <div
        key={k}
        className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800 py-1.5 text-sm last:border-0"
      >
        <span className="text-muted-foreground">{k}</span>
        <span className="font-medium text-gray-700 dark:text-gray-200 text-right">{v}</span>
      </div>
    ))}
  </div>
);
